import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Mail, Phone, Trash2, Hourglass, BellRing } from "lucide-react";
import { api } from "@/lib/api";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminWaitlist() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/waitlist").then(({ data }) => setData(data));

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/waitlist/${id}`);
      toast.success("Inscription retirée");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const notify = async (productId) => {
    try {
      const { data } = await api.post(`/admin/waitlist/${productId}/notify`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/waitlist/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "liste-attente.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export impossible");
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={26} />
        <span className="font-serif text-lg">Chargement…</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-waitlist-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Liste d'attente</h1>
          <p className="mt-1 text-sm text-bark-soft">
            Coordonnées des clients en attente sur les produits épuisés « Essaims » et « Reines ». {data.total} inscription(s).
          </p>
        </div>
        {data.total > 0 && (
          <button
            data-testid="waitlist-export"
            onClick={exportCsv}
            className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-5 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
          >
            <Download size={16} /> Exporter (CSV)
          </button>
        )}
      </div>

      {data.products.length === 0 ? (
        <div data-testid="waitlist-empty" className="rounded-2xl border border-amber-800/15 bg-white p-10 text-center">
          <Hourglass size={30} className="mx-auto text-stone-mute" />
          <p className="mt-3 font-serif text-xl text-bark">Aucune inscription pour l'instant</p>
          <p className="mt-1 text-sm text-bark-soft">Les demandes apparaîtront ici quand un essaim ou une reine sera épuisé·e.</p>
        </div>
      ) : (
        data.products.map((group) => (
          <div key={group.product_id} data-testid={`waitlist-group-${group.product_id}`} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="font-serif text-xl font-semibold text-bark">{group.product_name}</h2>
                <span className="text-xs uppercase tracking-[0.15em] text-stone-mute">{group.category}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-amber-deep/10 px-3 py-1 text-xs font-semibold text-amber-deep">
                  {group.entries.length} en attente
                </span>
                {group.in_stock && group.pending_count > 0 && (
                  <button
                    data-testid={`waitlist-notify-${group.product_id}`}
                    onClick={() => notify(group.product_id)}
                    className="flex items-center gap-1.5 rounded-full bg-sage px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-sage/85"
                    title="Envoyer un e-mail d'alerte de retour en stock aux inscrits non encore prévenus"
                  >
                    <BellRing size={13} /> Prévenir les inscrits
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-amber-900/10 text-left text-xs uppercase tracking-wider text-stone-mute">
                    <th className="pb-2 pr-4 font-medium">#</th>
                    <th className="pb-2 pr-4 font-medium">Nom</th>
                    <th className="pb-2 pr-4 font-medium">Coordonnées</th>
                    <th className="pb-2 pr-4 font-medium">Message</th>
                    <th className="pb-2 pr-4 font-medium">Inscrit le</th>
                    <th className="pb-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {group.entries.map((e, i) => (
                    <tr key={e.waitlist_id} data-testid={`waitlist-entry-${e.waitlist_id}`} className="border-b border-amber-900/5 align-top">
                      <td className="py-3 pr-4 text-stone-mute">{i + 1}</td>
                      <td className="py-3 pr-4 font-medium text-bark">
                        {e.name}
                        {e.notified && (
                          <span className="ml-2 rounded-full bg-sage/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sage">Prévenu·e</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-bark-soft">
                        <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-amber-deep">
                          <Mail size={13} /> {e.email}
                        </a>
                        {e.phone && (
                          <a href={`tel:${e.phone}`} className="mt-1 flex items-center gap-1.5 hover:text-amber-deep">
                            <Phone size={13} /> {e.phone}
                          </a>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-bark-soft">{e.message || "—"}</td>
                      <td className="py-3 pr-4 whitespace-nowrap text-stone-mute">{fmtDate(e.created_at)}</td>
                      <td className="py-3">
                        <button
                          data-testid={`waitlist-delete-${e.waitlist_id}`}
                          onClick={() => remove(e.waitlist_id)}
                          aria-label="Retirer"
                          className="rounded-full p-1.5 text-stone-mute transition-colors hover:bg-terra/10 hover:text-terra"
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
